import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation, Trans } from 'react-i18next';
import { storage } from '../utils/platform';
import { apiGet, apiPost, apiPatch } from '../utils/api-client';
import { formatMonthYear, formatShortMonth, formatNumber, formatPrice } from '../utils/format';

import DashboardLayout    from '../components/DashboardLayout/DashboardLayout';
import SwitchRestaurantModal from '../components/SwitchRestaurantModal/SwitchRestaurantModal';
import MetaHead           from '../components/MetaHead';
import RPSHero            from '../components/RPSHero/RPSHero';
import SinceLastVisit     from '../components/SinceLastVisit/SinceLastVisit';
import KPICard            from '../components/KPICard/KPICard';
import ActivationCockpit  from '../components/ActivationCockpit/ActivationCockpit';
import ImpactBusiness     from '../components/ImpactBusiness/ImpactBusiness';
import ObjectifPilote     from '../components/ObjectifPilote/ObjectifPilote';
import VoisinsCompare     from '../components/VoisinsCompare/VoisinsCompare';
import InsightsIA         from '../components/InsightsIA/InsightsIA';
import NoteEvolutionChart from '../components/NoteEvolutionChart/NoteEvolutionChart';
import { RESTO_TYPE_EMOJI } from '../constants/restoTypes';
import { pickConcurrentEmoji } from '../utils/concurrentEmoji';
import { PLAN_BY_ID, resolvePlanId } from '../constants/plans';

/* ============================================================
   DashboardV2 — page principale, branchée à GET /api/dashboard/
   ------------------------------------------------------------
   IDs DOM préservés pour le backend Express (rétrocompat scripts) :
     #kpi-rps · #kpi-rps-value
     #kpi-note · #kpi-note-value
     #kpi-satisfaits · #kpi-satisfaits-value
     #impact-couverts · #impact-ca · #impact-pertes
     #roi-total · #roi-multiple
     #objectif-barre · #objectif-chiffre
     #insights-ia · #voisins-compare · #noteChart
   ============================================================ */

/** Calcule le mois courant au format YYYY-MM (défaut du sélecteur). */
function currentMonthKey() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

/** Liste des 6 derniers mois pour le sélecteur (labels traduits via Intl). */
function buildMonthOptions(lang) {
  const out = [];
  const d = new Date();
  for (let i = 0; i < 6; i++) {
    const y = d.getFullYear();
    const m = d.getMonth() + 1;
    const key = `${y}-${String(m).padStart(2, '0')}`;
    // formatMonthYear → "avril 2026" / "April 2026" / "abril de 2026" / "aprile 2026"
    // Capitalize la première lettre (Intl en EN/FR/IT/ES renvoie souvent en minuscule).
    const label = formatMonthYear(key, lang);
    out.push({ value: key, label: label.charAt(0).toUpperCase() + label.slice(1) });
    d.setMonth(d.getMonth() - 1);
  }
  return out;
}

/**
 * Transforme la réponse `/api/dashboard/note-history` en couple
 * `{ labels, data }` consommable par NoteEvolutionChart.
 *
 * Labels = mois courts (3 lettres) dans la locale active (Intl.DateTimeFormat).
 *
 * Shape entrée : { history: [{ note: number|string, created_at: ISO }, ...] }
 *   - tri ASC déjà fait côté backend (ORDER BY created_at ASC LIMIT 30)
 *   - 30 points max
 *
 * Shape sortie :
 *   - labels : ['Nov', 'Déc', ...]  mois courts dans la langue active
 *   - data   : [4.2, 4.3, ...]      number (DECIMAL Postgres → string parfois)
 *
 * Si moins de 2 points → renvoie { labels: [], data: [] } pour déclencher
 * l'empty state du composant.
 */
function transformNoteHistory(history, lang) {
  if (!Array.isArray(history) || history.length < 2) {
    return { labels: [], data: [] };
  }
  const labels = [];
  const data   = [];
  for (const row of history) {
    const note = typeof row.note === 'number' ? row.note : parseFloat(row.note);
    if (!Number.isFinite(note)) continue;
    const d = new Date(row.created_at);
    if (Number.isNaN(d.getTime())) continue;
    // Mois court i18n : "Nov" en EN/FR, "nov." en ES, "nov" en IT
    labels.push(formatShortMonth(d, lang));
    data.push(note);
  }
  if (data.length < 2) return { labels: [], data: [] };
  return { labels, data };
}

export default function DashboardV2() {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation('dashboard');
  const lang = i18n.language || 'fr';

  const [month, setMonth]           = useState(currentMonthKey());
  const [data, setData]             = useState(null);
  const [loading, setLoading]       = useState(true);
  const [error, setError]           = useState(null);
  const [pausedBanner, setPausedBanner] = useState(false);
  const [gmbBanner, setGmbBanner] = useState(false);
  // [] explicite (et non undefined) pour déclencher l'empty state du composant
  // VoisinsCompare quand l'API ne renvoie pas de concurrents (cf hotfix 04/05/2026).
  const [neighbours, setNeighbours] = useState([]);
  const [switchOpen, setSwitchOpen] = useState(false);
  // [] explicite (et non undefined) pour déclencher l'empty state du composant
  // InsightsIA quand l'API n'a pas encore d'insights (cf hotfix 04/05/2026).
  const [insightsList, setInsightsList] = useState([]);
  const [syncing, setSyncing] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  // Historique de note Google pour NoteEvolutionChart — Hotfix 04/05/2026
  // (avant : mocks 4.2→4.7 hardcodés affichés à TOUS les restos).
  const [noteHistory, setNoteHistory] = useState({ labels: [], data: [], loading: true });
  // Cockpit d'activation : clé manuelle en cours de PATCH (désactive sa checkbox)
  const [togglingKey, setTogglingKey] = useState(null);

  /** Fetch des données dashboard pour le mois donné. */
  const loadDashboard = useCallback(async (monthKey) => {
    setLoading(true);
    setError(null);
    try {
      const payload = await apiGet(`/api/dashboard/?month=${monthKey}`);
      setData(payload);

      // Fetch l'historique de note Google pour NoteEvolutionChart (en parallèle)
      // Endpoint protégé JWT, retourne { history: [{ note, created_at }, ...] }
      // tri ASC, 30 points max. Ne bloque pas le rendu : empty state si vide.
      setNoteHistory((prev) => ({ ...prev, loading: true }));
      apiGet('/api/dashboard/note-history')
        .then((histData) => {
          const { labels, data } = transformNoteHistory(histData?.history, lang);
          setNoteHistory({ labels, data, loading: false });
        })
        .catch((e) => {
          console.warn('[Dashboard] note-history fetch failed:', e.message);
          // Empty state plutôt que mocks (cf hotfix 04/05/2026)
          setNoteHistory({ labels: [], data: [], loading: false });
        });

      // Fetch les insights IA (en parallèle, sans bloquer)
      // Hotfix 04/05/2026 — En cas de liste vide ou d'erreur réseau, on garde
      // [] explicite (pas undefined) → empty state honnête côté composant.
      apiGet('/api/dashboard/insights')
        .then((insData) => {
          const list = Array.isArray(insData?.insights) ? insData.insights : [];
          if (list.length === 0) {
            setInsightsList([]); // empty state honnête (avant : mocks)
            return;
          }
          // Mapping backend → format InsightsIA component (tone/icon/text/sub).
          // Chantier 3 — Paywall : les cartes locked (ins.locked === true)
          // ne contiennent QUE category + type + teaser. Les unlocked free
          // n'ont pas de `action` → sub se rabat sur la catégorie + un CTA Pro.
          const mapped = list.slice(0, 3).map((ins) => {
            const tone = (ins.type || '').toLowerCase();

            if (ins.locked) {
              return {
                tone: 'warning',
                icon: '🔒',
                text: <><strong>{ins.category || t('insights_ia.locked_title')}</strong> — {t('insights_ia.locked_title').toLowerCase()}</>,
                sub: ins.teaser ? `${ins.teaser}` : t('insights_ia.locked_default'),
                linkLabel: t('insights_ia.locked_cta'),
                onLinkClick: () => navigate('/abonnement'),
              };
            }

            let toneClass = 'warning';
            let icon = '⏰';
            if (tone === 'positive' || tone === 'positif') { toneClass = 'good'; icon = '✓'; }
            else if (tone === 'alert' || tone === 'negatif' || tone === 'negative') { toneClass = 'bad'; icon = '!'; }

            const hasAction = !!ins.action;
            return {
              tone: toneClass,
              icon,
              text: ins.constat || ins.titre || ins.description || t('insights_ia.fallback_text'),
              sub: hasAction
                ? ins.action
                : (ins.category ? t('insights_ia.category_prefix', { category: ins.category }) : ''),
              linkLabel: hasAction
                ? (toneClass === 'bad' ? t('insights_ia.alert_cta') : undefined)
                : t('insights_ia.unlock_action_cta'),
              onLinkClick: hasAction ? undefined : () => navigate('/abonnement'),
            };
          });
          setInsightsList(mapped);
        })
        .catch((e) => {
          console.warn('[Dashboard] insights fetch failed:', e.message);
          setInsightsList([]); // empty state plutôt que mocks
        });

      // Bandeau "abonnement suspendu" si user.account_status === 'suspended'
      if (payload?.user?.account_status === 'suspended' && payload?.user?.pending_reviews_count > 0) {
        setPausedBanner(true);
      } else {
        setPausedBanner(false);
      }

      // Bandeau "connecte GMB" si pas de google_place_id
      setGmbBanner(!payload?.restaurant?.google_place_id);

      // Voisins (concurrents) — uniquement si on a un place_id
      // Hotfix 04/05/2026 — On set [] explicitement (pas undefined) pour
      // déclencher l'empty state honnête du composant (avant : mocks Marseille).
      //
      // Chantier Découverte (30/07/2026) : le suivi concurrentiel est réservé aux
      // plans ayant la capacité `competitors`. On n'appelle même pas l'endpoint
      // sinon — il répondrait 403 à chaque chargement du dashboard (bruit dans les
      // logs + appel réseau inutile). Le composant affiche son empty state.
      const placeId = payload?.restaurant?.google_place_id;
      const canSeeCompetitors = payload?.capabilities
        ? payload.capabilities.competitors === true
        : true; // API sans `capabilities` (rolling deploy) → comportement d'avant
      if (placeId && canSeeCompetitors) {
        try {
          const compData = await apiGet(
            `/api/places/competitors?placeId=${encodeURIComponent(placeId)}&radius=1500&max=5`
          );
          const list = (compData?.competitors || []).map((c) => ({
            name:   c.name,
            emoji:  pickConcurrentEmoji(c, payload?.restaurant),
            // Note Google formatée selon la locale (virgule en FR/ES/IT, point en EN)
            rating: typeof c.rating === 'number'
              ? formatNumber(c.rating, lang, { minimumFractionDigits: 1, maximumFractionDigits: 1 })
              : '—',
            count:  t('v2.competitor_reviews_count', { count: c.reviews || 0 }),
            you:    !!c.isYou,
          }));
          setNeighbours(list); // [] si rien → empty state
        } catch (e) {
          console.warn('[Dashboard] competitors fetch failed:', e.message);
          setNeighbours([]); // empty state plutôt que mocks
        }
      } else {
        // Pas de place_id configuré, ou plan sans suivi concurrentiel
        // → empty state (le CTA d'upgrade vit dans l'onglet Concurrents).
        setNeighbours([]);
      }
    } catch (e) {
      setError(e.message || t('v2.error_loading'));
    } finally {
      setLoading(false);
    }
  }, [lang, navigate, t]);

  useEffect(() => {
    loadDashboard(month);
  }, [month, loadDashboard]);

  // Fetch le compteur de feedbacks non vus pour le badge sidebar
  useEffect(() => {
    let cancelled = false;
    apiGet('/api/dashboard/unread-feedbacks')
      .then((d) => { if (!cancelled) setUnreadCount(d?.count ?? 0); })
      .catch(() => { /* silencieux */ });
    return () => { cancelled = true; };
  }, []);

  const handleLogout = async () => {
    await storage.remove('token');
    window.location.href = '/login';
  };

  const handleMonthChange = (newMonth) => {
    setMonth(newMonth); // → useEffect refetch automatiquement
  };

  /** Sync Google : force un refresh des avis depuis Google Places + refetch dashboard */
  const handleSyncGoogle = useCallback(async () => {
    setSyncing(true);
    try {
      await apiPost('/api/dashboard/sync-google');
      await loadDashboard(month); // refetch toutes les données du dashboard
    } catch (e) {
      // eslint-disable-next-line no-alert
      alert(t('v2.sync_error_alert', { error: e.message }));
    } finally {
      setSyncing(false);
    }
  }, [loadDashboard, month, t]);

  /**
   * Coche/décoche une étape manuelle du cockpit d'activation.
   * Mise à jour optimiste du state local, puis PATCH ; la réponse contient
   * l'objet `activation` recalculé côté backend (source de vérité, mergé).
   * En cas d'erreur : refetch complet pour resynchroniser.
   */
  const handleActivationToggle = useCallback(async (key, done) => {
    setTogglingKey(key);
    // Optimiste : bascule l'étape localement en attendant le PATCH
    setData((prev) => {
      if (!prev?.activation) return prev;
      const steps = prev.activation.steps.map((s) => (s.key === key ? { ...s, done } : s));
      return {
        ...prev,
        activation: { ...prev.activation, steps, progress: steps.filter((s) => s.done).length },
      };
    });
    try {
      const res = await apiPatch('/api/restaurants/activation', { key, done });
      if (res?.activation) {
        setData((prev) => (prev ? { ...prev, activation: res.activation } : prev));
      }
    } catch (e) {
      console.warn('[Dashboard] activation toggle failed:', e.message);
      loadDashboard(month); // resync honnête si le PATCH a échoué
    } finally {
      setTogglingKey(null);
    }
  }, [loadDashboard, month]);

  /* ---------- Mappings backend → composants ---------- */
  const restaurant = data?.restaurant || {};
  const stats      = data?.stats      || {};
  const userInfo   = data?.user       || {};

  const rpsScore       = restaurant.rps_score ?? 0;
  // #3 volet A — garde-fou libellé : le palier superlatif "Excellence — top 10%"
  // n'est annoncé que si le backend confirme un échantillon suffisant (label_confident).
  // Défaut `true` si le champ manque (backend pas encore redéployé) → comportement legacy.
  const rpsLabelConfident = restaurant.rps_detail?.label_confident ?? true;
  // Sprint 2 P1 — vrai delta RPS 30j depuis backend (number signé ou null si <30j d'historique).
  // Pas de fallback côté front : RPSHero gère déjà le cas null = "Pas encore d'historique".
  // typeof + Number.isFinite pour discriminer null/undefined du nombre 0 légitime ("→ stable").
  const rpsDelta30d    = (typeof restaurant.rps_delta_30d === 'number' && Number.isFinite(restaurant.rps_delta_30d))
    ? restaurant.rps_delta_30d
    : null;
  const noteActuelle   = restaurant.note_actuelle ?? restaurant.note ?? 0;
  const nbAvis         = restaurant.nb_avis_total ?? restaurant.nb_avis ?? 0;
  const avisGagnes     = restaurant.avis_gagnes ?? 0;  // eslint-disable-line no-unused-vars
  const progressionPct = restaurant.progression_note ?? 0;

  const scansTotal     = stats.scans_total ?? 0;
  const scansAllTime   = stats.scans_all_time ?? 0;
  const satisfaits     = stats.satisfaits ?? 0;
  const feedbacks      = stats.feedbacks_negatifs ?? 0;
  const satisfactionPct = scansTotal > 0 ? Math.round((satisfaits / scansTotal) * 100) : 0;

  // Cockpit d'activation (reco #1) : affiché tant que le resto n'a pas son 1er scan.
  // Fail-safe : payload sans `activation` (backend pas encore déployé) → dashboard normal.
  const activation  = data?.activation || null;
  const showCockpit = !!activation && activation.activated === false;

  // Faux zéros (reco #2) : un taux (%) ou un montant (€) ne s'affiche jamais à
  // 0 trompeur — « — » / placeholder. Les compteurs entiers restent à 0 (légitime).
  const hasScansThisMonth = scansTotal > 0;

  const ticketMoyen    = parseFloat(restaurant.ticket_moyen) || 35;
  // Valeur générée (coef 0.3) et perte évitée (×3) — mêmes coefs que la formule backend.
  const ca             = Math.round(satisfaits * ticketMoyen * 0.3);
  // ×3 : perte estimée d'un client insatisfait qui ne revient pas + bouche-à-oreille négatif.
  const perteEvitee    = Math.round(feedbacks * ticketMoyen * 3);

  // Placeholder d'état vide : uniquement si AUCUNE donnée d'impact ce mois.
  // Bug audit 07/07 : avant, le placeholder du sous-titre ROI se déclenchait dès que
  // le ROI valait 0, alors qu'il y avait déjà de la valeur générée / de la perte évitée.
  const hasImpact = satisfaits > 0 || ca > 0 || perteEvitee > 0;

  // Prix d'abonnement dynamique (constants/plans.js) ; null si trial / plan inconnu → coût 0.
  // resolvePlanId : les comptes réels portent l'alias historique 'starter'
  // (et non 'essentiel') — sans résolution, le coût d'abonnement du ROI
  // retomberait à 0 pour tous les abonnés existants.
  const subscriptionPrice  = PLAN_BY_ID[resolvePlanId(userInfo?.plan)]?.price ?? null;
  const coutAbonnementMois = subscriptionPrice ?? 0;

  // ROI mensuel = valeur générée + perte évitée − coût d'abonnement.
  // Bug audit 07/07 : on lisait stats.roi_estime (backend) qui renvoyait 0 quand
  // ticket_moyen était NULL en base, alors que ca/perteEvitee utilisent un ticket
  // fallback à 35 € → « ROI +0 € » incohérent avec « +11 € / +105 € » affichés juste
  // au-dessus, et perte évitée ignorée (sous-vend le pare-feu). On calcule désormais le
  // ROI à partir des MÊMES montants affichés → cohérence garantie, perte évitée incluse.
  const roiEstime = ca + perteEvitee - coutAbonnementMois;

  const roiMultiple = (subscriptionPrice && subscriptionPrice > 0 && roiEstime > 0)
    ? (roiEstime / subscriptionPrice).toFixed(1)
    : null;

  // Sous-titre ROI (i18n) :
  //   1) aucune donnée d'impact ce mois → "vos premiers ROI…" (le bloc entier est de
  //      toute façon en mode `empty` dans ce cas, ce sous-titre n'est pas rendu)
  //   2) ROI ≤ 0 malgré des données      → "votre abonnement est bientôt rentabilisé"
  //   3) plan actif + ROI > 0            → "×N votre abonnement (X €)"
  //   4) trial / pas de plan             → "soit Y € de valeur générée ce mois"
  let roiSubtitle;
  if (!hasImpact) {
    roiSubtitle = t('v2.roi_subtitle_empty');
  } else if (roiEstime <= 0) {
    roiSubtitle = t('v2.roi_subtitle_soon');
  } else if (subscriptionPrice && roiMultiple) {
    // price est déjà formaté (symbole + décimales selon la locale) — le template
    // i18n ne contient plus de « € » en dur (cf. dashboard.json).
    roiSubtitle = t('v2.roi_subtitle_multiple', { multiple: roiMultiple, price: formatPrice(subscriptionPrice, lang) });
  } else {
    roiSubtitle = t('v2.roi_subtitle_value', { amount: formatNumber(roiEstime, lang) });
  }

  // Montant ROI signé (peut être négatif si le coût d'abonnement dépasse encore la valeur).
  const roiTotalLabel = `${roiEstime >= 0 ? '+' : '−'}${formatNumber(Math.abs(roiEstime), lang)} €`;

  const objectifAvis     = data?.objectif_avis ?? 15;
  const avisGeneres      = data?.avis_generes_revyo ?? 0;
  const sinceLastVisit   = data?.since_last_visit || null;
  const progressionPilote = data?.progression_pilote ?? 0;

  // Sous-titre RPS selon le score
  const rpsSubtitle =
    (rpsScore >= 80 && rpsLabelConfident) ? t('v2.rps_excellent')
    : rpsScore >= 60 ? t('v2.rps_good')
    : rpsScore >= 40 ? t('v2.rps_progressing')
    : t('v2.rps_to_improve');

  // Description ObjectifPilote selon la progression
  const objectifDescription =
    progressionPilote >= 100 ? t('v2.objectif_description_done', { goal: objectifAvis })
    : progressionPilote >= 60 ? t('v2.objectif_description_progress', { goal: objectifAvis })
    : t('v2.objectif_description_start', { goal: objectifAvis });

  // Label mois affiché dans le subtitle + le titre ImpactBusiness
  const moisLabel = formatMonthYear(data?.mois_actif || month, lang);
  const moisLabelShort = formatShortMonth(
    (data?.mois_actif || month) + '-01',
    lang
  );

  // Titre voisins
  const voisinsTitle = restaurant.ville
    ? t('v2.voisins_title_with_city', { city: restaurant.ville })
    : t('v2.voisins_title');

  /* ---------- Render ---------- */
  return (
    <>
    <MetaHead page="dashboard" />
    <DashboardLayout
      sidebarProps={{
        restaurant: {
          name: restaurant.nom || t('sidebar.default_resto_name'),
          location: restaurant.ville ? `${restaurant.ville}` : '',
          // Emoji dynamique selon type_cuisine (cf constants/restoTypes.js)
          emoji: RESTO_TYPE_EMOJI[restaurant.type_cuisine] || '🍽️',
        },
        user: {
          // User dynamique depuis data.user (backend /api/dashboard renvoie prenom + nom)
          initials: ((userInfo.prenom?.[0] || '') + (userInfo.nom?.[0] || '')).toUpperCase() || '?',
          name: `${userInfo.prenom || ''}${userInfo.nom ? ' ' + userInfo.nom[0] + '.' : ''}`.trim() || t('sidebar.default_user_fallback'),
          role: t('sidebar.default_user_role'),
        },
        badges: { '/collecte': unreadCount },
        onLogout: handleLogout,
        onSwitchRestaurant: () => setSwitchOpen(true),
      }}
      topbarProps={{
        greeting: userInfo.prenom
          ? t('topbar.greeting_hello_name', { name: userInfo.prenom })
          : t('topbar.greeting_hello'),
        subtitle: loading
          ? t('topbar.loading')
          : t('topbar.report_for', { month: moisLabel.toLowerCase() }),
        months: buildMonthOptions(lang),
        selectedMonth: month,
        onMonthChange: handleMonthChange,
        onCtaClick: () => navigate('/collecte'),
      }}
    >
      {/* Erreur de chargement */}
      {error && (
        <div className="revyo-banner revyo-banner-warning" style={{ gridColumn: '1 / -1' }}>
          <span>⚠ {error}</span>
          <button type="button" className="revyo-banner-cta" onClick={() => loadDashboard(month)}>
            {t('v2.error_retry')}
          </button>
        </div>
      )}

      {/* Banners contextuels */}
      {pausedBanner && (
        <div id="paused-reviews-banner" className="revyo-banner revyo-banner-warning" style={{ gridColumn: '1 / -1' }}>
          <span>
            <Trans
              i18nKey="dashboard:v2.banner_paused"
              values={{ count: userInfo.pending_reviews_count }}
              components={[<strong id="paused-count" />]}
            />
          </span>
          <button id="paused-cta" type="button" className="revyo-banner-cta" onClick={() => navigate('/abonnement')}>
            {t('v2.banner_paused_cta')}
          </button>
        </div>
      )}
      {gmbBanner && (
        <div id="gmb-connect-banner" className="revyo-banner revyo-banner-info" style={{ gridColumn: '1 / -1' }}>
          <span>{t('v2.banner_gmb')}</span>
          <button type="button" className="revyo-banner-cta" onClick={() => navigate('/configuration')}>
            {t('v2.banner_gmb_cta')}
          </button>
        </div>
      )}

      <SinceLastVisit data={sinceLastVisit} />

      {/* Cockpit d'activation (reco #1) — remplace les blocs "faux zéros"
          (Satisfaction %, Impact business, Objectif pilote) tant que le resto
          n'a pas son premier scan. Les blocs à vraie donnée (Note Google,
          Voisins, Insights, Évolution) restent affichés. */}
      {showCockpit && (
        <ActivationCockpit
          activation={activation}
          slug={restaurant.slug}
          goal={objectifAvis}
          onToggle={handleActivationToggle}
          togglingKey={togglingKey}
        />
      )}

      {/* RPS hero (large gauche, row-2) — delta 30j honnête (Sprint 2 P1).
          On passe le nombre brut signé (ou null) ; RPSHero formate ▲/▼/→/empty. */}
      <RPSHero
        score={rpsScore}
        delta={rpsDelta30d}
        subtitle={rpsSubtitle}
      />

      <KPICard
        id="kpi-note"
        valueId="kpi-note-value"
        label={t('v2.kpi_note_label')}
        icon="⭐"
        value={noteActuelle
          ? formatNumber(noteActuelle, lang, { minimumFractionDigits: 1, maximumFractionDigits: 1 })
          : t('v2.kpi_value_empty')}
        showStar
        delta={<span id="kpi-note-sync">{t('v2.kpi_note_sync')}</span>}
        onDeltaClick={handleSyncGoogle}
        deltaLoading={syncing}
        sub={
          <Trans
            i18nKey="dashboard:v2.kpi_note_sub"
            values={{ count: nbAvis }}
            components={[<span id="kpi-avis-total" />]}
          />
        }
        accent
      />

      {/* Satisfaction : masquée en mode cockpit (faux zéro trompeur).
          Reco #2 : 0 client ce mois → « — » sans cœur vert, jamais « 0 % ». */}
      {!showCockpit && (
        <KPICard
          id="kpi-satisfaits"
          valueId="kpi-satisfaits-value"
          label={t('v2.kpi_satisfaction_label')}
          icon={hasScansThisMonth ? '💚' : undefined}
          value={hasScansThisMonth ? `${satisfactionPct}%` : t('v2.kpi_value_empty')}
          delta={hasScansThisMonth ? t('v2.kpi_satisfaction_delta', { count: satisfaits }) : undefined}
          sub={hasScansThisMonth
            ? t('v2.kpi_satisfaction_sub', { total: scansTotal })
            : t('v2.kpi_satisfaction_empty_sub')}
        />
      )}

      <KPICard
        id="kpi-scans"
        valueId="kpi-scans-value"
        label={t('v2.kpi_scans_label')}
        icon="📲"
        value={formatNumber(scansAllTime, lang)}
        sub={scansAllTime > 0
          ? t('v2.kpi_scans_sub', { count: scansTotal })
          : t('v2.kpi_counter_empty_hint')}
      />

      <KPICard
        id="kpi-avis-generes"
        valueId="kpi-avis-generes-value"
        label={t('v2.kpi_avis_generes_label')}
        icon="✍️"
        value={formatNumber(avisGeneres, lang)}
        sub={avisGeneres > 0
          ? t('v2.kpi_avis_generes_sub')
          : t('v2.kpi_counter_empty_hint')}
      />

      {/* Réorg bento : ObjectifPilote (rowSpan 2) à gauche, ImpactBusiness (rowSpan 2) à droite — 0 trou.
          Mode cockpit : les deux sont masqués — l'objectif pilote est rappelé DANS le cockpit,
          et l'impact business à +0 € est un faux zéro démoralisant. */}
      {!showCockpit && (
        <ObjectifPilote
          current={avisGeneres}
          goal={objectifAvis}
          description={objectifDescription}
        />
      )}

      {!showCockpit && (
        <ImpactBusiness
          title={t('v2.impact_title', { month: moisLabelShort.toLowerCase() })}
          empty={!hasImpact}
          emptyText={t('impact_business.empty_state')}
          couverts={`+${satisfaits}`}
          ca={`+${formatNumber(ca, lang)} €`}
          pertesEvitees={`+${formatNumber(perteEvitee, lang)} €`}
          roiTotal={roiTotalLabel}
          roiSubtitle={roiSubtitle}
        />
      )}

      <VoisinsCompare
        title={voisinsTitle}
        neighbours={neighbours}
      />

      <InsightsIA
        insights={insightsList}
        badge={insightsList.length > 0
          ? t('insights_ia.default_badge', { count: insightsList.length })
          : null}
      />
      <NoteEvolutionChart
        labels={noteHistory.labels}
        data={noteHistory.data}
        loading={noteHistory.loading}
        current={noteActuelle
          ? formatNumber(noteActuelle, lang, { minimumFractionDigits: 1, maximumFractionDigits: 1 })
          : t('v2.kpi_value_empty')}
        delta={
          noteHistory.data.length >= 2
            ? (progressionPct >= 0
                ? t('v2.note_chart_delta_up', { value: formatNumber(progressionPct, lang, { minimumFractionDigits: 0, maximumFractionDigits: 1 }) })
                : t('v2.note_chart_delta_down', { value: formatNumber(progressionPct, lang, { minimumFractionDigits: 0, maximumFractionDigits: 1 }) }))
            : ''
        }
      />
    </DashboardLayout>
    {switchOpen && <SwitchRestaurantModal onClose={() => setSwitchOpen(false)} />}
    </>
  );
}
